import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

export interface PostItem {
  id: number;
  category: string;
  title: string;
  author: string;
  views: number;
  likes: number;
  comments: number;
  date: string;
}

export function usePosts(categoryLabel?: string) {
  const [posts, setPosts] = useState<PostItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      let query = supabase
        .from("posts")
        .select("id, title, category, views, created_at, author_id, profiles(nickname)");

      if (categoryLabel) {
        query = query.eq("category", categoryLabel);
      }

      const { data, error: fetchError } = await query
        .order("created_at", { ascending: false })
        .limit(50);

      if (fetchError) {
        setError(`게시글을 불러오는 중 오류가 발생했습니다: ${fetchError.message || fetchError}`);
        setPosts([]);
        return;
      }

      if (!data || data.length === 0) {
        setPosts([]);
        return;
      }

      const postIds = data.map((p) => p.id);

      // Get comment counts
      const { data: commentCounts } = await supabase
        .from("comments")
        .select("post_id")
        .in("post_id", postIds);

      const commentCountMap: Record<number, number> = {};
      if (commentCounts) {
        commentCounts.forEach((c) => {
          commentCountMap[c.post_id] = (commentCountMap[c.post_id] || 0) + 1;
        });
      }

      // Get like counts from post_likes
      const { data: likeData } = await supabase
        .from("post_likes")
        .select("post_id")
        .in("post_id", postIds);

      const likeCountMap: Record<number, number> = {};
      if (likeData) {
        likeData.forEach((l) => {
          likeCountMap[l.post_id] = (likeCountMap[l.post_id] || 0) + 1;
        });
      }

      const mapped: PostItem[] = data.map((p) => ({
        id: p.id,
        category: p.category,
        title: p.title,
        author: (p.profiles as unknown as { nickname: string })?.nickname || "익명",
        views: p.views || 0,
        likes: likeCountMap[p.id] || 0,
        comments: commentCountMap[p.id] || 0,
        date: new Date(p.created_at).toISOString().split("T")[0],
      }));

      setPosts(mapped);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(`게시글을 불러오는 중 오류가 발생했습니다: ${msg}`);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [categoryLabel]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  return { posts, loading, error, refetch: fetchPosts };
}